import type { dashboard } from '@cwos/api-client';
import type { Severity } from '@cwos/ui-shared';

/**
 * Pure derivations for the Executive Dashboard (Task 20). DOM-free so every
 * label/tone decision is unit-tested independently of rendering. The backend
 * owns every number; these helpers only decide how an already-computed value is
 * presented — they never fabricate a metric.
 */

export type DashboardPeriod = dashboard.DashboardPeriod;
export type TrendDirection = dashboard.TrendDirection;

/** Accept only a real dashboard period; anything else falls back (default 7d). */
export function normalizePeriod(
  raw: string | undefined,
  fallback: DashboardPeriod = '7d',
): DashboardPeriod {
  if (raw === '7d' || raw === '30d') return raw;
  return fallback;
}

/** Number of days a period spans (used for human copy, never for math). */
export function periodDays(period: DashboardPeriod): number {
  return period === '30d' ? 30 : 7;
}

/** Clamp an expected-weekly-hours input to the API's legal range (1–168). */
export function clampExpectedHours(value: number): number {
  if (!Number.isFinite(value)) return 40;
  return Math.min(168, Math.max(1, Math.round(value)));
}

// --- Service levels ---------------------------------------------------------
// For hours, "up" is good (more coverage). For exceptions, "up" is bad (more
// alerts) — so the two trend interpreters are deliberately separate.

export function serviceTrendLabel(direction: TrendDirection): string {
  return direction === 'up' ? 'Improving' : direction === 'down' ? 'Declining' : 'Steady';
}

export function serviceTrendTone(direction: TrendDirection): Severity {
  return direction === 'up' ? 'good' : direction === 'down' ? 'look' : 'plain';
}

export function exceptionTrendLabel(direction: TrendDirection): string {
  return direction === 'up' ? 'Rising' : direction === 'down' ? 'Falling' : 'Steady';
}

export function exceptionTrendTone(direction: TrendDirection): Severity {
  return direction === 'up' ? 'look' : direction === 'down' ? 'good' : 'plain';
}

// --- Care gaps --------------------------------------------------------------

export type GapSeverity = dashboard.ClientCareGap['severity'];

export function gapSeverityLabel(severity: GapSeverity): string {
  switch (severity) {
    case 'severe':
      return 'Severe';
    case 'moderate':
      return 'Moderate';
    default:
      return 'Minor';
  }
}

export function gapSeverityTone(severity: GapSeverity): Severity {
  switch (severity) {
    case 'severe':
      return 'urgent';
    case 'moderate':
      return 'look';
    default:
      return 'plain';
  }
}

// --- Exceptions -------------------------------------------------------------

export function alertSeverityTone(severity: string): Severity {
  switch (severity) {
    case 'critical':
      return 'urgent';
    case 'warning':
      return 'look';
    case 'info':
      return 'unsure';
    default:
      return 'plain';
  }
}

// --- Reconciliation ---------------------------------------------------------

export function reconciliationItemLabel(itemType: string): string {
  switch (itemType) {
    case 'event_without_session':
      return 'Visit without time record';
    case 'session_without_event':
      return 'Time record without visit';
    case 'minute_mismatch':
      return 'Minute mismatch';
    default:
      return itemType;
  }
}

export function reconciliationItemTone(itemType: string): Severity {
  return itemType === 'minute_mismatch' || itemType === 'event_without_session' ? 'look' : 'plain';
}

// --- Documentation ----------------------------------------------------------

export function docStateLabel(isLate: boolean): string {
  return isLate ? 'Late' : 'Open';
}

export function docStateTone(isLate: boolean): Severity {
  return isLate ? 'look' : 'plain';
}

// --- Integration health -----------------------------------------------------

export function integrationTone(health: dashboard.IntegrationHealth): Severity {
  switch (health.state) {
    case 'connected':
      return 'good';
    case 'connecting':
      return 'unsure';
    case 'action_required':
      return 'urgent';
    case 'temporarily_delayed':
      return 'look';
    // Syncing fine, but history is not all in yet — a real gap, not a
    // failure. Same quiet treatment as `temporarily_delayed`: nothing is
    // broken, something is still owed.
    case 'historical_import_incomplete':
      return 'look';
    case 'no_integrations':
      return 'plain';
    default:
      return 'plain';
  }
}

export function integrationStateLabel(state: dashboard.IntegrationState): string {
  switch (state) {
    case 'connected':
      return 'Connected';
    case 'connecting':
      return 'Connecting';
    case 'action_required':
      return 'Action required';
    case 'temporarily_delayed':
      return 'Temporarily delayed';
    case 'historical_import_incomplete':
      return 'History not fully imported';
    case 'no_integrations':
      return 'No integrations configured';
    default:
      return 'Unknown';
  }
}

// --- Numeric formatting -----------------------------------------------------

/**
 * A whole percent.
 *
 * `null` means the figure is UNDEFINED, not zero and not hidden — the mean of an
 * empty set, which is what `averagePercent` sends when there is nobody to
 * measure. It renders as an em dash beside the `0` staff count that explains it;
 * writing `0%` there would assert total non-delivery and `100%` (which the API
 * used to send) would assert the opposite.
 */
export function percentLabel(value: number | null): string {
  if (value == null) return '—';
  return `${value}%`;
}

export function hoursLabel(hours: number): string {
  return `${hours}h`;
}

/** A gap ratio (0–1) as a whole-percent string. */
export function gapRatioLabel(ratio: number): string {
  return `${Math.round(ratio * 100)}%`;
}
